import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiCallError, callFunction } from '@/lib/api';
import { usePatientPicker } from '@/hooks/usePatientPicker';
import { formatDanishDateTime } from '@/lib/danishDateTime';

type Row = {
  id: string;
  kind: string;
  version_code: string | null;
  letter_identifier: string | null;
  latest_envelope_identifier: string | null;
  received_at: string | null;
  import_status: string;
  refuse_code: string | null;
  refuse_reason: string | null;
  patient_name: string | null;
  patient_display_name: string | null;
  patient_id_kind: 'cpr' | 'erstatnings_cpr' | 'none';
  match_status: 'matched' | 'unmatched' | 'manual';
  is_copy: boolean;
  consent_given: boolean | null;
};

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : formatDanishDateTime(date);
}

function AttachLabPatient({
  clinicId,
  row,
  onDone,
}: {
  clinicId: string;
  row: Row;
  onDone: () => void;
}) {
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: patients } = usePatientPicker(clinicId, search);
  async function attach(patientId: string) {
    setBusy(true);
    setError(null);
    try {
      await callFunction('communicate-lab-import', {
        action: 'attach',
        clinic_id: clinicId,
        report_id: row.id,
        patient_id: patientId,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Svaret kunne ikke knyttes til patienten.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-2 rounded-md border border-ds-amber-line bg-ds-amber-tint p-3 text-sm">
      <p className="font-medium">
        {row.patient_id_kind === 'erstatnings_cpr'
          ? 'Erstatnings-CPR — knyt manuelt til en patient (kun admin)'
          : 'Ingen patientmatch — knyt manuelt (kun admin)'}
      </p>
      <input
        className="w-full rounded-md border border-ds-field px-3 py-1.5 text-sm"
        placeholder="Søg patient"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {error && (
        <p role="alert" className="text-ds-red-ink">
          {error}
        </p>
      )}
      <ul className="space-y-1">
        {(patients ?? []).map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2">
            <span>{`${p.first_name} ${p.last_name}`}</span>
            <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={() => void attach(p.id)}>
              Knyt
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReceivedLabReportsPanel({
  clinicId,
  kind,
  refreshKey,
  canAttach = false,
}: {
  clinicId: string;
  kind?: 'XRPT01' | 'XRPT05';
  refreshKey?: number;
  canAttach?: boolean;
}) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await callFunction<Record<string, unknown>, { reports: Row[] }>(
        'communicate-lab-import',
        { action: 'list', clinic_id: clinicId },
      );
      setRows(result.reports.filter((r) => !kind || r.kind === kind));
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : 'Laboratoriesvar kunne ikke hentes.');
    } finally {
      setLoading(false);
    }
  }, [clinicId, kind]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          Modtagne laboratoriesvar
          {kind ? ` (${kind})` : ''}
        </h3>
        <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? 'Opdaterer…' : 'Opdatér'}
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-ds-red-ink">
          {error}
        </p>
      )}
      {rows === null && loading && (
        <div className="flex flex-col gap-s3" aria-busy>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {rows && rows.length === 0 && !loading && (
        <p className="text-sm text-ds-muted">Ingen laboratoriesvar indlæst endnu.</p>
      )}
      {rows && rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.id} className="rounded-md border border-ds-line p-3 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium">
                  {row.kind} {row.version_code ?? ''}
                  {row.is_copy ? ' · kopisvar' : ''}
                  {row.consent_given === false ? ' · nægtet samtykke' : ''}
                </p>
                <span className="text-xs text-ds-muted">{formatTime(row.received_at)}</span>
              </div>
              <p className="mt-1 text-ds-text">
                {row.patient_display_name || row.patient_name || 'Ukendt patient'}
                {row.patient_id_kind === 'erstatnings_cpr' ? ' (erstatnings-CPR)' : ''}
                {row.match_status === 'unmatched' ? ' — ikke matchet' : ''}
              </p>
              <p className="mt-1 text-xs text-ds-muted">
                Brev {row.letter_identifier || '—'} · Kuvert {row.latest_envelope_identifier || '—'}
                {row.import_status === 'rejected' && row.refuse_code
                  ? ` · afvist (${row.refuse_code})`
                  : ''}
                {row.import_status === 'resend' ? ' · genfremsendt' : ''}
              </p>
              {canAttach && row.match_status === 'unmatched' && row.import_status !== 'rejected' && (
                <div className="mt-3">
                  <AttachLabPatient clinicId={clinicId} row={row} onDone={() => void load()} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
